import { Component, ReactNode, FocusEvent, ChangeEvent, SyntheticEvent } from 'react';
import classnames from 'classnames';
import SV from 'server-vars';
import { FormattedMessage } from 'dibs-react-intl';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { Input, InputProps } from 'dibs-elements/exports/Input';
import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';
import { symbols } from './constants';

import styles from './styles/CurrencyInput.scss'; // data passed to consumer on blur and change

const isCurrency = (val: unknown): val is keyof typeof symbols =>
    Object.keys(symbols).some(key => key === val);

type CurrencyInputState = {
    value: string;
    cursorIndex: number;
    isFocused: boolean;
};

export type VatConfiguration = {
    priceBookName: string | null;
    defaultVatRate: string | null;
} | null;

export type EventHandlerData<E extends SyntheticEvent> = {
    currencySymbol: string;
    formattedValue: string;
    event: E;
};

export type CurrencyInputProps = Pick<
    InputProps,
    | 'dataTn'
    | 'autoFocus'
    | 'disabled'
    | 'errorMessageAlignment'
    | 'hasError'
    | 'horizontalSpacing'
    | 'autoComplete'
    | 'className'
    | 'errorDataTn'
    | 'errorMessage'
    | 'name'
    | 'placeholder'
    | 'size'
    | 'label'
    | 'inputRef'
> & {
    value?: string | number | null;
    currency?: string | null;
    showAbbreviation?: boolean;
    showCurrencySymbol?: boolean;
    selectOnFocus?: boolean;
    allowDecimal?: boolean;
    vatConfiguration?: VatConfiguration;
    alignRight?: boolean;
    currencySymbolClass?: string;
    currencySymbolDataTn?: string;
    includedTaxRate?: number | null;
    vatRateConfig?: string | null;
    locale?: string;
    highlightColor?: string;
    onChange?: (value: string, data: EventHandlerData<ChangeEvent<HTMLInputElement>>) => void;
    onBlur?: (value: string, data: EventHandlerData<FocusEvent<HTMLInputElement>>) => void;
    onFocus?: () => void;
};

export class CurrencyInput extends Component<CurrencyInputProps, CurrencyInputState> {
    static defaultProps = {
        autoFocus: false,
        currency: 'USD',
        disabled: false,
        errorMessageAlignment: 'right',
        hasError: false,
        horizontalSpacing: 'default',
        showAbbreviation: true,
        showCurrencySymbol: true,
        onFocus: (): void => {},
        selectOnFocus: true,
    };

    constructor(props: CurrencyInputProps) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.inputNode = null;
        this.state = {
            value: this.normalizeInput(props.value || ''),
            cursorIndex: 0,
            isFocused: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: CurrencyInputProps): void {
        const newValue = nextProps.value || '';
        const normalizeValue = this.normalizeInput(newValue);

        if (this.state.value !== normalizeValue) {
            this.setState({
                value: normalizeValue,
            });
        }
    }

    componentDidUpdate(): void {
        const node = this.inputNode;
        /* istanbul ignore next - mount doesn't handle refs */

        if (node && document && document.activeElement === node) {
            const { number: formattedNumber } = this.formatInput(this.state.value);
            node.value = formattedNumber === '0' && !node.value ? '' : formattedNumber;
            node.selectionStart = node.selectionEnd = this.state.cursorIndex;
        }
    }

    inputNode: HTMLInputElement | null;

    normalizeInput = (incomingValue: string | number | null | undefined): string => {
        const { allowDecimal } = this.props;
        const regexValue = String(incomingValue).match(/^\d*\.?\d*/);
        const value = regexValue ? regexValue[0] : '';

        if (allowDecimal) {
            return value ? parseFloat(value).toFixed(2) : '0.00';
        } else {
            return value ? parseFloat(value).toFixed(0) : '';
        }
    };
    formatIntlInput = (
        value: string,
        config?: { allowDecimal?: boolean }
    ): {
        number: string;
        abbreviation: keyof typeof symbols;
        currencySymbol: string;
    } => {
        const { currency, allowDecimal, locale } = { ...this.props, ...config };
        const formatLocale = locale || SV.get('locale') || 'en-US'; // there's some case where empty currency could be passed

        const checkedCurrency = isCurrency(currency) ? currency : 'USD'; // use resolvedOptions to figure out the appropriate number of fraction digits
        // for the passed currency

        const resolvedOptions = new Intl.NumberFormat(formatLocale, {
            style: 'currency',
            currency: checkedCurrency,
        }).resolvedOptions();
        const currencySymbol = symbols[checkedCurrency]; // format without {style: currency} since we will add the symbol and
        // abbreviation to the input prefix/suffix directly

        const number = new Intl.NumberFormat(formatLocale, {
            minimumFractionDigits: allowDecimal ? resolvedOptions.minimumFractionDigits : 0,
            maximumFractionDigits: allowDecimal ? resolvedOptions.maximumFractionDigits : 0, // $FlowFixMe - string type should be number, requires a decent amount of refactoring
        }).format(parseFloat(value || '0'));

        return {
            number,
            abbreviation: checkedCurrency,
            currencySymbol,
        };
    };
    formatInput = (value: string): ReturnType<typeof this.formatIntlInput> => {
        return this.formatIntlInput(value);
    };
    formatVatAmount = (value: string, vatPercentage: number): string => {
        return this.formatIntlInput(
            String(parseFloat(value || '0') * (vatPercentage / 100 + 1)),
            {
                allowDecimal: true,
            } // must allow decimals for VAT calculations
        ).number;
    };
    getRawNumbersAsString = (str: string): string => {
        const parsedString = str.replace(/[^\d]/g, '');

        if (parsedString) {
            if (this.props.allowDecimal) {
                return (parseInt(parsedString) / 100).toFixed(2);
            }

            return parseInt(parsedString).toString();
        }

        return parsedString;
    };
    handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
        this.setState({ ...this.state, isFocused: false });
        const rawValue = this.getRawNumbersAsString(e.target.value);
        const { currencySymbol, number: formattedValue } = this.formatInput(this.state.value);
        /* istanbul ignore else */

        if (this.props.onBlur) {
            this.props.onBlur(rawValue, {
                currencySymbol,
                formattedValue,
                event: e,
            });
        }
    };
    handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
        this.setState({ ...this.state, isFocused: true }); // on focus, select the entire given input.

        /* istanbul ignore else */

        if (!this.inputNode) {
            this.inputNode = e.target;
        }

        const node = this.inputNode;
        /* istanbul ignore else */

        if (this.props.selectOnFocus && node && document && document.activeElement === node) {
            // ECOM-2337 zero timeout is needed to prevent losing selection
            // after additional focus events
            setTimeout(function () {
                node.selectionStart = 0;
                node.selectionEnd = node.value.length;
            });
        }

        this.props.onFocus?.();
    };
    handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        this.inputNode = e.target;
        const { number: formattedStateValue } = this.formatInput(this.state.value);
        let newValue = e.target.value;
        let index = e.target.selectionStart || 0; // Handle backspace on "," or "."

        if (newValue.length === formattedStateValue.length - 1) {
            /* istanbul ignore if */
            if (!(parseInt(formattedStateValue[index]) >= 0)) {
                newValue = newValue.slice(0, index - 1) + newValue.slice(index);
                index = index - 2;

                if (index < 0) {
                    index = 0;
                }
            }
        }

        const rawValue = this.getRawNumbersAsString(newValue);
        const { currencySymbol, number: formattedInputValue } = this.formatInput(rawValue);
        /* istanbul ignore else */

        if (this.props.onChange) {
            this.props.onChange(rawValue, {
                currencySymbol,
                formattedValue: formattedInputValue,
                event: e,
            });
        }
        /* istanbul ignore else */

        if (formattedStateValue !== newValue) {
            const distanceFromEnd = newValue.length - index;
            let nextIndex = formattedInputValue.length - distanceFromEnd;
            /* istanbul ignore if */

            if (nextIndex < 0) {
                nextIndex = 0;
            }

            this.setState({ ...this.state, value: rawValue, cursorIndex: nextIndex });
        }
    };

    render(): ReactNode {
        const {
            alignRight,
            allowDecimal,
            autoComplete,
            autoFocus,
            vatConfiguration,
            className,
            dataTn,
            disabled,
            errorDataTn,
            errorMessage,
            errorMessageAlignment,
            hasError,
            horizontalSpacing,
            name,
            placeholder,
            size,
            label,
            showAbbreviation,
            showCurrencySymbol,
            currencySymbolClass,
            vatRateConfig,
            inputRef,
            highlightColor,
        } = this.props;
        const priceBookName = vatConfiguration?.priceBookName;
        const includedTaxRate =
            this.props.includedTaxRate ?? parseFloat(String(vatConfiguration?.defaultVatRate)); // ?? by design - must allow 0 as valid includedTaxRate

        const { value } = this.state;
        const {
            currencySymbol,
            abbreviation,
            number: formattedValue,
        } = this.formatInput(value || '');
        let displayVal = '';

        if (allowDecimal || parseInt(value) >= 0) {
            displayVal = formattedValue;
        }

        const inputClassNames = classnames(
            {
                [styles.alignRight]: alignRight,
            },
            className
        );
        const inputErrorDataTn = errorDataTn ? errorDataTn : `${dataTn}-error`;
        const currencySymbolDataTn = this.props.currencySymbolDataTn || `${dataTn}-currency-symbol`;
        return (
            <>
                <Input
                    inputRef={inputRef}
                    type={allowDecimal ? '' : 'tel'}
                    autoFocus={autoFocus}
                    className={inputClassNames}
                    dataTn={dataTn}
                    disabled={disabled}
                    errorDataTn={inputErrorDataTn}
                    errorMessage={errorMessage}
                    errorMessageAlignment={errorMessageAlignment}
                    hasError={hasError}
                    horizontalSpacing={horizontalSpacing}
                    highlightColor={highlightColor}
                    leftDecorator={
                        showCurrencySymbol && (
                            <span
                                data-tn={currencySymbolDataTn}
                                className={classnames(styles.currency, currencySymbolClass)}
                            >
                                {currencySymbol}
                            </span>
                        )
                    }
                    maxLength={17}
                    name={name}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    onFocus={this.handleFocus}
                    placeholder={placeholder}
                    rightDecorator={
                        showAbbreviation && (
                            <span data-tn="currency-abbreviation">{abbreviation}</span>
                        )
                    }
                    showIcons={false}
                    size={size}
                    value={displayVal}
                    label={label}
                    autoComplete={autoComplete}
                />

                {priceBookName && !isNaN(includedTaxRate) && vatRateConfig !== 'UNREGISTERED' && (
                    <Tooltip
                        isVisible={this.state.isFocused}
                        direction={'bottom'}
                        type="autoWidth"
                        paddingSize="small"
                    >
                        <div className={styles.tooltipBody}>
                            <strong>
                                <FormattedMessage
                                    id="CurrencyInput.VatTooltip.Description"
                                    defaultMessage="Prices Buyers Will See"
                                />
                            </strong>

                            <hr />
                            <p>
                                {vatMessages.buyersIncl({ priceBookName })}
                                <span className={styles.strong}>
                                    {currencySymbol}
                                    {this.formatVatAmount(this.state.value, includedTaxRate)}
                                </span>
                            </p>
                            <hr />
                            <p>
                                <FormattedMessage
                                    id="CurrencyInput.VatTooltip.SecondLine"
                                    defaultMessage="Rest of the World"
                                />

                                <span className={styles.strong}>
                                    {currencySymbol}
                                    {this.formatVatAmount(this.state.value, 0)}
                                </span>
                            </p>
                        </div>
                    </Tooltip>
                )}
            </>
        );
    }
}
